import { type FC, type PropsWithChildren, useEffect, useMemo } from 'react';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import { getVisibility } from 'utils/authentication';
import { parseCustomerSignature } from 'utils/customerSignature';
import { createTypeOptions } from 'utils/objectTypes';
import { Form, TreeSelect, Typography } from '@timeedit/ui-components/antd';
import './ObjectPreviewFilter.scss';

const LinkToObjectManager: FC<
  PropsWithChildren<{ params: Record<string, unknown> }>
> = (props) => {
  const user = useAppSelector((state) => state.auth.user);

  const path = useMemo(() => {
    const signature = parseCustomerSignature(user?.customerSignature);
    if (!signature) return null;

    const params = new URLSearchParams();
    Object.entries(props.params).forEach(([key, value]) => {
      params.append(key, String(value));
    });

    return `/${signature}/objects?${params}`;
  }, [user, props.params]);

  const hasAccess = useMemo(
    () =>
      getVisibility({
        authUser: user,
        minimumPermissions: { scopes: ['TE_ADMIN::admin'] },
      }),
    [user]
  );

  if (!hasAccess || !path) {
    return null;
  }

  return (
    <Typography.Link underline href={path} target="_blank">
      {props.children}
    </Typography.Link>
  );
};

export type TObjectPreviewFilterState = {
  typeId: number | null;
};

type TObjectPreviewFilterProps = {
  value: TObjectPreviewFilterState;
  queries: Record<string, unknown>;
  onChange: (value: TObjectPreviewFilterState) => void;
  showLinkToObject: boolean;
};

export const ObjectPreviewFilter: FC<TObjectPreviewFilterProps> = (props) => {
  const allObjectTypes = useAppSelector(selectObjectTypesList);
  const objectTypeOptions = useMemo(
    () => createTypeOptions(allObjectTypes),
    [allObjectTypes]
  );

  useEffect(() => {
    if (props.value.typeId != null || allObjectTypes.length === 0) {
      return;
    }

    const next: TObjectPreviewFilterState = {
      ...props.value,
      typeId: allObjectTypes?.[0].id,
    };

    props.onChange(next);
  }, [props.value, allObjectTypes, props.onChange]);

  const handleTypeChange = (value: string) => {
    const next: TObjectPreviewFilterState = {
      ...props.value,
      typeId: parseInt(value, 10),
    };
    props.onChange(next);
  };

  return (
    <div className="objects-preview-filter">
      <Form layout="vertical" style={{ padding: '0px 0px 10px 10px' }}>
        <Form.Item label="Object Type">
          <TreeSelect
            treeData={objectTypeOptions}
            value={props.value.typeId?.toString() ?? null}
            onChange={handleTypeChange}
            popupMatchSelectWidth={false}
            treeDefaultExpandedKeys={objectTypeOptions.map((opt) => opt.value)}
          />
        </Form.Item>
      </Form>
      {props.showLinkToObject && (
        <LinkToObjectManager params={props.queries}>
          Manage objects
        </LinkToObjectManager>
      )}
    </div>
  );
};
